<template>
  <form class="record-editor" @submit.prevent="submit">
    <h3>{{ title }}</h3>

    <template v-if="type === 'document'">
      <label><span>CPF</span><input v-model.trim="form.cpf" maxlength="14" inputmode="numeric" placeholder="000.000.000-00"></label>
      <label><span>RG</span><input v-model.trim="form.rg" maxlength="25"></label>
      <label>
        <span>Órgão emissor</span>
        <select v-model="form.rgEmissor">
          <option value="">Selecione</option>
          <option
            v-if="hasLegacyIssuer"
            :value="String(record.rgEmissor)"
            disabled
          >
            Órgão não cadastrado (ID {{ record.rgEmissor }})
          </option>
          <option v-for="issuer in issuingAuthorityOptions" :key="issuer.value" :value="issuer.value">
            {{ issuer.label }}
          </option>
        </select>
      </label>
      <label><span>Data de expedição do RG</span><input v-model="form.rgDataExpedicao" type="date"></label>
      <label><span>Passaporte</span><input v-model.trim="form.passaporteNumero" maxlength="20"></label>
      <label><span>País do passaporte</span><input v-model.trim="form.passaportePais" maxlength="30"></label>
    </template>

    <template v-else-if="type === 'address'">
      <label class="wide">
        <span>CEP</span>
        <input
          v-model.trim="form.cep"
          maxlength="9"
          inputmode="numeric"
          autocomplete="postal-code"
          aria-describedby="person-address-cep-status"
          @input="scheduleCepLookup"
        >
        <small id="person-address-cep-status" :class="{ 'field-error': cepError }" aria-live="polite">
          <template v-if="cepLoading">Consultando CEP...</template>
          <template v-else-if="cepError">{{ cepError }}</template>
          <template v-else-if="cepResolved">Endereço preenchido a partir do CEP.</template>
          <template v-else>Informe os 8 números para preencher o endereço.</template>
        </small>
      </label>
      <label class="wide"><span>Logradouro</span><input v-model.trim="form.logradouro" maxlength="255" autocomplete="street-address"></label>
      <label><span>Número</span><input v-model="form.numero" maxlength="6" inputmode="numeric" pattern="[0-9]*" @input="updateAddressNumber"></label>
      <label><span>Complemento</span><input v-model.trim="form.complemento" maxlength="80"></label>
      <label><span>Bairro</span><input v-model.trim="form.bairro" maxlength="45"></label>
      <label><span>UF</span><select v-model.number="form.idUf" @change="clearCity"><option value="">Selecione</option><option v-for="state in options.states" :key="state.value" :value="state.value">{{ state.label }} — {{ state.nome }}</option></select></label>
      <label class="wide"><span>Cidade</span><input v-model.trim="citySearch" type="search" list="person-city-options" autocomplete="off" placeholder="Digite ao menos 2 caracteres" @input="scheduleCitySearch" @change="selectCity"><datalist id="person-city-options"><option v-for="city in cityOptions" :key="city.value" :value="city.label">{{ city.state }}</option></datalist><small v-if="cityLoading">Buscando cidades...</small><small v-else-if="citySearch.length >= 2 && !cityOptions.length">Nenhuma cidade encontrada.</small></label>
    </template>

    <template v-else>
      <label class="wide"><span>Contato</span><input v-model.trim="form.conteudo" required maxlength="255"></label>
      <label><span>Complemento</span><input v-model.trim="form.complemento" maxlength="80"></label>
      <label><span>Tipo de contato</span><select v-model.number="form.idTipoContato"><option value="">Selecione</option><option v-for="contactType in options.contact_types" :key="contactType.value" :value="contactType.value">{{ contactType.label }}</option></select></label>
    </template>

    <label class="reason"><span>Justificativa da alteração</span><textarea v-model.trim="reason" required minlength="5" maxlength="500" rows="3"></textarea></label>
    <div class="actions">
      <button type="button" class="btn btn-secondary" :disabled="saving" @click="$emit('cancel')">Cancelar</button>
      <button type="submit" class="btn btn-primary" :disabled="saving || reason.length < 5">{{ saving ? 'Salvando...' : 'Salvar registro' }}</button>
    </div>
  </form>
</template>

<script>
import PessoasService from '@/services/pessoas.service'
import { ADDRESS_NUMBER_ERROR, isAddressNumberValid } from '@/services/address-number.service'

const fields = {
  document: ['idDocumento', 'cpf', 'rg', 'rgEmissor', 'rgDataExpedicao', 'passaporteNumero', 'passaportePais'],
  address: ['idEndereco', 'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'idCidade', 'idUf'],
  contact: ['idContato', 'conteudo', 'complemento', 'idTipoContato']
}

export default {
  name: 'RecordEditor',
  emits: ['cancel', 'save'],
  props: {
    type: { type: String, required: true },
    record: { type: Object, default: () => ({}) },
    options: { type: Object, default: () => ({ contact_types: [], states: [] }) },
    saving: { type: Boolean, default: false }
  },
  data () {
    return {
      form: {},
      reason: '',
      citySearch: '',
      cityOptions: [],
      cityLoading: false,
      cityTimer: null,
      cepLoading: false,
      cepError: '',
      cepResolved: false,
      cepTimer: null,
      cepRequestId: 0
    }
  },
  computed: {
    title () {
      const editing = Boolean(this.form.idDocumento || this.form.idEndereco || this.form.idContato)
      const label = { document: 'documento', address: 'endereço', contact: 'contato' }[this.type]
      return `${editing ? 'Editar' : 'Novo'} ${label}`
    },
    issuingAuthorityOptions () {
      return (this.options.issuing_authorities || []).map(issuer => ({
        ...issuer,
        value: String(issuer.value)
      }))
    },
    hasLegacyIssuer () {
      const value = this.record.rgEmissor
      return value !== null && value !== undefined && value !== '' &&
        !this.issuingAuthorityOptions.some(issuer => issuer.value === String(value))
    }
  },
  beforeUnmount () {
    clearTimeout(this.cityTimer)
    clearTimeout(this.cepTimer)
  },
  watch: {
    record: {
      immediate: true,
      deep: true,
      handler () {
        this.form = Object.fromEntries((fields[this.type] || []).map(field => [field, this.dateInput(field, this.record[field])]))
        this.citySearch = this.record.cidade || ''
        this.reason = ''
      }
    },
    type () {
      this.form = Object.fromEntries((fields[this.type] || []).map(field => [field, this.dateInput(field, this.record[field])]))
      this.citySearch = this.record.cidade || ''
      this.reason = ''
    }
  },
  methods: {
    updateAddressNumber (event) {
      event.target.value = event.target.value.replace(/[^0-9]/g, '').slice(0, 6)
      this.form.numero = event.target.value
    },
    submit () {
      if (this.type === 'address' && !isAddressNumberValid(this.form.numero)) {
        window.toaster.emit('open', { style: 'error', message: ADDRESS_NUMBER_ERROR, floater: true })
        return
      }
      const data = { ...this.form }
      if (this.type === 'document' && this.hasLegacyIssuer && String(data.rgEmissor) === String(this.record.rgEmissor)) {
        delete data.rgEmissor
      }
      this.$emit('save', { type: this.type, data, reason: this.reason })
    },
    dateInput (field, value) {
      return field.toLowerCase().includes('data') && value ? String(value).slice(0, 10) : (value ?? '')
    },
    scheduleCitySearch () {
      clearTimeout(this.cityTimer)
      this.form.idCidade = null
      if (this.citySearch.length < 2) {
        this.cityOptions = []
        return
      }
      this.cityTimer = setTimeout(this.searchCities, 300)
    },
    async searchCities () {
      this.cityLoading = true
      try {
        const response = await PessoasService.searchCities({ search: this.citySearch, state_id: this.form.idUf || undefined })
        this.cityOptions = response.data.data || []
      } catch (error) {
        this.cityOptions = []
      } finally {
        this.cityLoading = false
      }
    },
    selectCity () {
      const selected = this.cityOptions.find(city => city.label === this.citySearch)
      this.form.idCidade = selected ? selected.value : null
      if (selected && !this.form.idUf) this.form.idUf = selected.state_id
    },
    clearCity () {
      this.citySearch = ''
      this.cityOptions = []
      this.form.idCidade = null
    },
    scheduleCepLookup () {
      clearTimeout(this.cepTimer)
      this.cepRequestId += 1
      this.cepError = ''
      this.cepResolved = false

      const cep = String(this.form.cep || '').replace(/\D/g, '')
      if (cep.length !== 8) {
        this.cepLoading = false
        return
      }

      this.cepTimer = setTimeout(() => this.lookupCep(cep), 350)
    },
    async lookupCep (cep) {
      const requestId = ++this.cepRequestId
      this.cepLoading = true
      this.cepError = ''

      try {
        const response = await PessoasService.getAddressByCep(cep)
        if (requestId !== this.cepRequestId) return

        const address = response.data?.endereco
        if (!address || address.erro === true || address.erro === 'true') {
          this.cepError = 'CEP não encontrado.'
          return
        }

        this.form.cep = address.cep || this.form.cep
        if (address.logradouro) this.form.logradouro = address.logradouro
        if (address.complemento) this.form.complemento = address.complemento
        if (address.bairro) this.form.bairro = address.bairro
        this.form.idUf = address.idUf || null
        this.form.idCidade = address.idCidade || null
        this.citySearch = address.cidade || ''
        this.cityOptions = address.idCidade
          ? [{ value: address.idCidade, label: address.cidade, state_id: address.idUf, state: address.uf }]
          : []
        this.cepResolved = true
      } catch (error) {
        if (requestId === this.cepRequestId) {
          this.cepError = 'Não foi possível consultar o CEP. Tente novamente.'
        }
      } finally {
        if (requestId === this.cepRequestId) this.cepLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.record-editor { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $mg_small; margin-bottom: $hmg_mid; padding: $hmg_small; background: $color-bg; border: $border-component; border-radius: $border-radius-mid; }
h3, .wide, .reason, .actions { grid-column: 1 / -1; }
h3 { margin: 0; font-size: 16px; }
label { display: flex; flex-direction: column; gap: $hmg_mini; font-weight: 600; }
input, textarea, select { width: 100%; min-height: 44px; padding: $mg_mini $mg_small; border: $border-component; border-radius: $border-radius-mid; background: $color-secondary; color: $color-dev; }
input:focus-visible, textarea:focus-visible, select:focus-visible, button:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
small { color: $color-text; font-weight: 400; }
.field-error { color: $color-error; }
.actions { display: flex; justify-content: flex-end; gap: $mg_mini; }
@media (max-width: 640px) {
  .record-editor { grid-template-columns: 1fr; }
  h3, .wide, .reason, .actions { grid-column: auto; }
  .actions { flex-direction: column-reverse; }
  .actions .btn { width: 100%; }
}
</style>
